import classNames from "classnames";
import { LeafletMouseEvent } from "leaflet";
import { observer } from "mobx-react-lite";
import { useRootStore } from "../models/Root";
import { ISensorDisplayStream } from "../models/SensorDisplay";
import { IStreamSensor } from "../models/StreamSensor";
import DivIconMarker from "./DivIconMarker";
import StreamMarker, { StreamM } from "./StreamMarker";

interface StreamSensorViewProps {
  sensor: IStreamSensor;
  display: ISensorDisplayStream;
}

const StreamSensorView = observer(
  ({ sensor, display }: StreamSensorViewProps) => {
    const { period, measure } = display;
    const {
      menus: { panel },
      sensors: { setSensor, selected },
    } = useRootStore();

    const isSelected = selected && selected.sensor.id === sensor.id;

    const { id: idx, position } = sensor;

    const container = {
      tagName: "div",
      className: classNames("selectable-marker", {
        "is-selected": isSelected,
      }),
    };

    const eventHandlers =
      panel === "measure"
        ? {}
        : {
            click: (e: LeafletMouseEvent) => {
              setSensor(sensor);
            },
          };
    const marker = { position, eventHandlers };

    switch (measure) {
      case "stage":
        const stage = sensor.stage;
        return (
          <DivIconMarker key={idx} marker={marker} container={container}>
            {stage === null || stage.latest === null ? (
              <StreamM />
            ) : (
              <StreamMarker
                value={sensor.stageFor(period)?.value}
                unit={stage.unit}
                measure={measure}
              ></StreamMarker>
            )}
          </DivIconMarker>
        );

      case "discharge":
        const discharge = sensor.discharge;
        return (
          <DivIconMarker key={idx} marker={marker} container={container}>
            {discharge === null || discharge?.latest === null ? (
              <StreamM />
            ) : (
              <StreamMarker
                value={sensor.dischargeFor(period)?.value}
                unit={discharge.unit}
                measure={measure}
              ></StreamMarker>
            )}
          </DivIconMarker>
        );
    }
  }
);

const StreamSensorsLayer = ({ display }: { display: ISensorDisplayStream }) => {
  const {
    sensors: { streamSensors },
  } = useRootStore();

  const period = display.period;
  const sorter = (a: IStreamSensor, b: IStreamSensor) =>
    (a.stageFor(period)?.value || 0) - (b.stageFor(period)?.value || 0);
  return (
    <>
      {streamSensors.sort(sorter).map((sensor) => (
        <StreamSensorView
          key={sensor.id}
          sensor={sensor}
          display={display}
        ></StreamSensorView>
      ))}
    </>
  );
};

export default observer(StreamSensorsLayer);
